// First-visit walkthrough: dims the page, lights up one control at a time and says what it does. Used by the proof
// editor (its own steps below) and the editor's final proof page (steps passed in). Skippable at any point (button,
// Esc); remembered per link in localStorage; "How this works" replays it.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// show: run before the step is lit (e.g. switch to the tab its target is on); such a step is kept even if its
// target isn't on the page yet.
export type TourStep = { target: string | null; title: string; body: string; reveal?: boolean; show?: () => void };
type Step = TourStep;

const STEPS: Step[] = [
  { target: null, title: "A quick tour", body: "Takes about a minute: how to change words, make italics or bold, fix a line break, check your footnotes, see your changes and send them. Skip it any time." },
  { target: ".proof-blk", title: "Change words right in the text", body: "Each paragraph (or stanza) has its own box. Click into one and type: delete, retype, whatever you like. Return makes a new line." },
  { target: ".proof-toolbar", title: "Italics, bold and Undo", body: "Select some words, then press I for italic or B for bold (Cmd/Ctrl+I and Cmd/Ctrl+B work too). Undo takes back your last change, as many times as you like." },
  { target: ".proof-struct", title: "Wrong paragraph break?", body: "Click into a paragraph and press ↑ Join with above to pull it onto the one before. To break one in two, click where it should break and press Split here." },
  { target: ".proof-fn-list", title: "Your footnotes", body: "Correct a footnote's words here. Its small number in the text stays where it is; if a footnote should move or go, say so in your note." },
  { target: ".proof-tab-edits", title: "My edits", body: "Every change you've made, listed with the words crossed out and added. Click one to jump straight to it." },
  { target: ".proof-meter", title: "Your page limit", body: "This shows how much of your page you've used. If it goes over, trim a little before sending." },
  { target: ".proof-send", title: "When you're done", body: "Press Send my changes. You can add a note for the editor just above. Nothing is sent until you press it." },
];

const KEY = (token: string, prefix: string) => `${prefix}:${token}`;
export const tourSeen = (token: string, prefix = "proof-edit-tour") => { try { return localStorage.getItem(KEY(token, prefix)) === "1"; } catch { return false; } };
const markSeen = (token: string, prefix: string) => { try { localStorage.setItem(KEY(token, prefix), "1"); } catch { /* private window: it just shows again */ } };

type Box = { top: number; left: number; width: number; height: number };

export function EditorTour({ token, onClose, steps: given, prefix = "proof-edit-tour" }: { token: string; onClose: () => void; steps?: TourStep[]; prefix?: string }) {
  const [steps] = useState(() => (given ?? STEPS).filter((s) => s.target === null || s.show || document.querySelector(s.target)));
  const [i, setI] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardH, setCardH] = useState(200);
  const step = steps[i];
  const last = i === steps.length - 1;

  const close = useCallback(() => { markSeen(token, prefix); onClose(); }, [token, prefix, onClose]);

  const measure = useCallback(() => {
    if (!step?.target) { setBox(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setBox(null); return; }
    const r = el.getBoundingClientRect();
    setBox({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // A step whose target is on another tab switches to it first; the target is lit once it has rendered.
  useLayoutEffect(() => {
    if (!step?.show) return;
    step.show();
    const t = window.setTimeout(() => {
      const el = step.target ? document.querySelector(step.target) : null;
      if (el) el.scrollIntoView({ block: "center", behavior: "auto" });
      measure();
    }, 80);
    return () => window.clearTimeout(t);
  }, [step, measure]);

  useLayoutEffect(() => {
    const el = step?.target ? document.querySelector(step.target) : null;
    // A target taller than the screen (the photograph tray on a phone) is lined up with the top; otherwise it's centred.
    if (el) el.scrollIntoView({ block: el.getBoundingClientRect().height > window.innerHeight * 0.5 ? "start" : "center", behavior: "auto" });
    measure();
  }, [step, measure]);

  // The ＋ Photo spots normally only appear on hover, so show the lit one for this step.
  useLayoutEffect(() => {
    if (!step?.reveal || !step.target) return;
    const el = document.querySelector(step.target);
    el?.classList.add("tour-reveal");
    measure();
    const t = window.setTimeout(measure, 220); // the spot animates open; re-measure once it has
    return () => { window.clearTimeout(t); el?.classList.remove("tour-reveal"); };
  }, [step, measure]);

  useLayoutEffect(() => { if (cardRef.current) setCardH(cardRef.current.offsetHeight); }, [step, i]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight" || e.key === "Enter") setI((n) => Math.min(n + 1, steps.length - 1));
      else if (e.key === "ArrowLeft") setI((n) => Math.max(n - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [close, measure, steps.length]);

  if (!step) return null;

  const pad = 6;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const narrow = vw < 640;
  const W = Math.min(340, vw - 24);
  // Only the part of the target that is actually on screen gets the ring.
  const vis = box && (() => {
    const top = Math.max(box.top, 8);
    const bottom = Math.min(box.top + box.height, vh - 8);
    return bottom > top ? { top, left: box.left, width: box.width, height: bottom - top } : null;
  })();
  const tall = !!box && box.height > vh * 0.5;
  let style: React.CSSProperties;
  if (!vis) {
    style = narrow ? { left: 12, right: 12, bottom: 12, width: "auto" } : { left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: W };
  } else if (narrow || tall) {
    // Tall target: keep the card pinned inside the screen's bottom edge so it can never leave the page.
    style = narrow ? { left: 12, right: 12, bottom: 12, width: "auto" } : { left: Math.max(12, Math.min(vis.left, vw - W - 12)), bottom: 12, width: W };
  } else {
    const left = Math.max(12, Math.min(vis.left, vw - W - 12));
    const below = vis.top + vis.height + pad + 10 + cardH + 12 <= vh;
    const top = below ? vis.top + vis.height + pad + 10 : Math.max(12, vis.top - pad - 10 - cardH);
    style = { left, top: Math.min(top, vh - cardH - 12), width: W };
  }

  return (
    <div className="tour-root" role="dialog" aria-modal="true" aria-label="Quick tour">
      {vis ? (
        <div className="tour-hole" style={{ top: vis.top - pad, left: vis.left - pad, width: vis.width + pad * 2, height: vis.height + pad * 2 }} />
      ) : (
        <div className="tour-dim" />
      )}
      <div className="tour-card" ref={cardRef} style={style}>
        <small className="tour-count">{i + 1} of {steps.length}</small>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
        <div className="tour-actions">
          <button type="button" className="proof-link" onClick={close}>{last ? "Close" : "Skip tour"}</button>
          <span>
            {i > 0 && <button type="button" className="proof-btn proof-btn--edit" onClick={() => setI(i - 1)}>Back</button>}
            <button type="button" className="proof-btn proof-btn--ok" onClick={() => (last ? close() : setI(i + 1))}>{last ? "Got it" : i === 0 ? "Show me" : "Next"}</button>
          </span>
        </div>
      </div>
    </div>
  );
}
